import { baseApi } from '../../../shared/api/baseApi';
import type { ApiPath } from '../../../shared/api/types';
import { notifyOnError } from '../../../shared/api/onQueryError';
import type { Organization } from '../model/types';

export const ORGANIZATIONS_PAGE_SIZE = 100;

interface OrganizationsResponse {
  count: number;
  data: Organization[];
  page_num: number;
  pages: number;
}

const unwrap = (response: OrganizationsResponse | Organization[]): Organization[] =>
  Array.isArray(response) ? response : response.data ?? [];

export const organizationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizations: builder.query<Organization[], { query?: string }>({
      async queryFn({ query }, _api, _extraOptions, fetchWithBQ) {
        const searchQuery = query ?? '';

        const firstResult = await fetchWithBQ({
          url: '/api/organizations' satisfies ApiPath,
          params: {
            query: searchQuery,
            page_size: ORGANIZATIONS_PAGE_SIZE,
            page_num: 1,
          },
        });

        if (firstResult.error) {
          return { error: firstResult.error };
        }

        const firstPage = firstResult.data as OrganizationsResponse;
        const organizations = unwrap(firstPage);

        if (firstPage.pages <= 1) {
          return { data: organizations };
        }

        const pageRequests = Array.from(
          { length: firstPage.pages - 1 },
          (_, index) =>
            fetchWithBQ({
              url: '/api/organizations' satisfies ApiPath,
              params: {
                query: searchQuery,
                page_size: ORGANIZATIONS_PAGE_SIZE,
                page_num: index + 2,
              },
            }),
        );

        const results = await Promise.all(pageRequests);

        const failedResult = results.find((result) => result.error);

        if (failedResult?.error) {
          return { error: failedResult.error };
        }

        for (const result of results) {
          organizations.push(...unwrap(result.data as OrganizationsResponse));
        }

        return { data: organizations };
      },
      providesTags: ['Organizations'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(
          queryFulfilled,
          'Не удалось загрузить список организаций',
        );
      },
    }),

    /** Для администратора — все организации платформы. */
    getOrganizationsAdmin: builder.query<Organization[], { query?: string }>({
      async queryFn({ query }, _api, _extraOptions, fetchWithBQ) {
        const searchQuery = query ?? '';

        const firstResult = await fetchWithBQ({
          url: '/api/admin/organizations' satisfies ApiPath,
          params: {
            query: searchQuery,
            page_size: ORGANIZATIONS_PAGE_SIZE,
            page_num: 1,
          },
        });

        if (firstResult.error) {
          return { error: firstResult.error };
        }

        const firstPage = firstResult.data as OrganizationsResponse;
        const organizations = unwrap(firstPage);

        if (firstPage.pages <= 1) {
          return { data: organizations };
        }

        const pageRequests = Array.from(
          { length: firstPage.pages - 1 },
          (_, index) =>
            fetchWithBQ({
              url: '/api/admin/organizations' satisfies ApiPath,
              params: {
                query: searchQuery,
                page_size: ORGANIZATIONS_PAGE_SIZE,
                page_num: index + 2,
              },
            }),
        );

        const results = await Promise.all(pageRequests);

        const failedResult = results.find((result) => result.error);

        if (failedResult?.error) {
          return { error: failedResult.error };
        }

        for (const result of results) {
          organizations.push(...unwrap(result.data as OrganizationsResponse));
        }

        return { data: organizations };
      },
      providesTags: ['Organizations'],
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(
          queryFulfilled,
          'Не удалось загрузить список организаций',
        );
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetOrganizationsQuery,
  useGetOrganizationsAdminQuery,
} = organizationApi;